import styled, { keyframes } from "styled-components";

// Animations
export const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const fadeOutDown = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(30px);
  }
`;

export const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const slideOutLeft = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-50px);
  }
`;

export const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Styled Components
export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

export const Header = styled.header`
  position: fixed;
  top: 0;
  width: 100%;
  background: rgba(254, 252, 249, 0.98);
  backdrop-filter: blur(15px);
  border-bottom: 1px solid var(--accent-light);
  z-index: 1000;
  padding: 20px 0;
  box-shadow: var(--shadow-soft);
`;

export const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const Logo = styled.a`
  font-family: "Source Code Pro", monospace;
  font-size: 28px;
  font-weight: 700;
  color: var(--accent);
  text-decoration: none;
  letter-spacing: 2px;
  text-transform: uppercase;

  &::before {
    content: "♡ ";
    color: var(--accent-dark);
    font-size: 24px;
  }

  &::after {
    content: " ♡";
    color: var(--accent-dark);
    font-size: 24px;
  }
`;

export const Nav = styled.nav`
  ul {
    display: flex;
    list-style: none;
    gap: 40px;
  }

  a {
    text-decoration: none;
    color: var(--text-secondary);
    font-weight: 500;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 1px;
    transition: all 0.3s ease;
    position: relative;
    padding: 10px 0;

    &:hover {
      color: var(--accent);
    }
  }
`;

export const CTAButton = styled.a`
  background: var(--gradient-primary);
  color: var(--surface);
  padding: 15px 35px;
  border: none;
  border-radius: 30px;
  font-weight: 600;
  font-family: "Source Code Pro", monospace;
  text-decoration: none;
  transition:
    color 0.3s ease,
    box-shadow 0.3s ease,
    transform 0.3s ease;

  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 1px;
  box-shadow: var(--shadow-soft);

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-medium);
    background: var(--accent-dark);
    color: var(--surface);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      transform: none;
      box-shadow: var(--shadow-soft);
    }
  }
`;

export const PageHeader = styled.section`
  background: var(--surface);
  padding-top: 20px;
  margin-top: 75px;
  text-align: center;

  padding-bottom: 30px;

  h1 {
    font-size: 48px;
    font-weight: 700;
    margin-bottom: 20px;
    text-transform: uppercase;
    letter-spacing: 2px;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
      font-size: 36px;
    }
  }

  p {
    font-size: 16px;
    color: var(--text-muted);
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.8;
  }

  @media (max-width: 920px) {
    margin-top: 66.7px;
  }
`;

export const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--text-muted);
  font-size: 14px;
  text-transform: uppercase;

  a {
    color: var(--text-muted);
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: var(--accent);
    }
  }
`;

export const CartSection = styled.section`
  padding: 80px 0;
  min-height: 60vh;
`;

export const CartGrid = styled.div`
  display: grid;
  grid-template-columns: ${(props) =>
    props.$isCartEmpty ? "1fr" : "1fr 400px"};
  gap: 60px;
  align-items: start;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

export const CartItems = styled.div`
  background: var(--surface);
  border-radius: 25px;
  padding: 40px;
  border: 2px solid var(--accent-light);
  box-shadow: var(--shadow-soft);
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

export const CartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--accent-light);
  flex-wrap: wrap;

  h2 {
    font-size: 24px;
    font-weight: 700;
    text-transform: uppercase;
    color: var(--text-primary);
  }
`;

export const ClearCartButton = styled.button`
  background: transparent;
  border: 2px solid var(--accent-light);
  color: var(--accent);
  padding: 10px 20px;
  border-radius: 20px;
  font-family: inherit;
  font-size: 12px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;

  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: var(--gradient-primary);
    color: var(--surface);
    border-color: var(--accent);
  }
`;

export const CartItem = styled.div`
  display: flex;
  gap: 20px;
  padding: 30px 0;
  border-bottom: 1px solid var(--accent-light);
  align-items: center;
  animation: ${(props) => (props.$removing ? slideOutLeft : slideInRight)} 0.3s
    ease forwards;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

export const ItemImage = styled.img`
  width: auto;
  height: 88.8px;
  object-fit: cover;

  border-radius: 15px;
  border: 2px solid var(--accent-light);
  flex-shrink: 0;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    width: 100%;
    height: 150px;
  }
`;

export const ItemDetails = styled.div`
  flex: 1;
`;

export const ItemName = styled.h4`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--text-primary);
  text-transform: uppercase;
`;

export const ItemDescription = styled.p`
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 10px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Maksimalno 2 reda */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ItemTags = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const ItemTag = styled.span`
  background: var(--surface);
  color: var(--accent);
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 10px;
  text-transform: uppercase;
  border: 1px solid var(--accent-light);
`;

export const ItemControls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 15px;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

export const ItemPrice = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: var(--accent);
`;

export const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--surface);
  border-radius: 20px;
  padding: 8px 15px;
  border: 2px solid var(--accent-light);
  transition: all 0.3s ease;
`;

export const QuantityButton = styled.button`
  background: none;
  border: none;
  color: var(--accent);
  font-size: 16px;
  cursor: pointer;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: var(--accent);
    color: var(--surface);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const QuantityDisplay = styled.span`
  font-weight: 600;
  min-width: 30px;
  text-align: center;
  color: var(--text-primary);
`;

export const RemoveButton = styled.button`
  background: none;
  border: none;
  color: var(--accent);
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  transition: all 0.3s ease;
  font-size: 16px;

  &:hover {
    background: var(--accent);
    color: var(--surface);
  }
`;

export const EmptyCart = styled.div`
  text-align: center;
  padding: 80px 40px;
  color: var(--text-muted);
  animation: ${fadeInUp} 0.6s ease forwards;

  i {
    font-size: 64px;
    margin-bottom: 30px;
    color: var(--accent);
  }

  h3 {
    font-size: 24px;
    margin-bottom: 15px;
    color: var(--text-secondary);
  }

  p {
    margin-bottom: 30px;
    line-height: 1.6;
  }
`;

export const CartSummary = styled.div`
  background: var(--surface);
  border-radius: 25px;
  padding: 40px;
  border: 2px solid var(--accent-light);
  box-shadow: var(--shadow-soft);
  position: sticky;
  top: 150px;
  animation: ${scaleIn} 0.6s ease forwards;
  transition: all 0.3s ease;

  @media (max-width: 968px) {
    position: static;
  }

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

export const SummaryHeader = styled.h3`
  font-size: 20px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 30px;
  color: var(--text-primary);
  text-align: center;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--accent-light);
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid var(--accent-light);

  &:last-of-type {
    border-bottom: 2px solid var(--accent-light);
    margin-bottom: 20px;
  }

  &.total-row {
    font-size: 24px;
    font-weight: 700;
    color: var(--accent);
    margin-bottom: 30px;
    padding-top: 20px;
  }
`;

export const SummaryLabel = styled.span`
  color: var(--text-secondary);
  font-size: 14px;
  text-transform: uppercase;

  &.total {
    color: var(--accent);
    font-size: 18px;
    font-weight: 700;
  }
`;

export const SummaryValue = styled.span`
  font-weight: 600;
  color: var(--text-primary);

  &.total {
    color: var(--accent);
    font-size: 24px;
    font-weight: 700;
  }

  &.discount {
    color: var(--success);
  }
`;

export const PromoCode = styled.div`
  margin-bottom: 30px;
`;

export const PromoInput = styled.input`
  width: 100%;
  padding: 15px 20px;
  border: 2px solid var(--accent-light);
  border-radius: 25px;
  font-family: inherit;
  font-size: 14px;
  background: var(--surface);
  margin-bottom: 15px;
  transition: border-color 0.3s ease;

  text-transform: uppercase;
  &:focus {
    outline: none;
    border-color: var(--accent);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const PromoButton = styled.button`
  width: 100%;
  background: transparent;
  border: 2px solid var(--accent-light);
  color: var(--accent);
  padding: 12px;
  border-radius: 25px;
  font-family: inherit;
  font-size: 12px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: var(--accent);
    color: var(--surface);
    border-color: var(--accent);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: var(--success);
    border-color: var(--success);
    color: var(--surface);

    &:hover {
      background: var(--success);
      color: var(--surface);
    }
  }
`;

export const CheckoutButton = styled.button`
  width: 100%;
  background: var(--gradient-primary);
  color: var(--surface);
  border: none;
  padding: 20px;
  border-radius: 30px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    color 0.3s ease,
    box-shadow 0.3s ease,
    transform 0.3s ease;

  box-shadow: var(--shadow-soft);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-medium);
    background: var(--accent-dark);
    color: var(--surface);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      transform: none;
      box-shadow: var(--shadow-soft);
      background: var(--gradient-primary);
      color: var(--text-primary);
    }
  }

  @media (max-width: 768px) {
    font-size: 14px;
    flex-wrap: wrap;
  }
`;

export const ContinueShoppingLink = styled.a`
  width: 100%;
  background: transparent;
  border: 2px solid var(--accent-light);
  color: var(--text-secondary);
  padding: 15px;
  border-radius: 25px;
  font-family: inherit;
  font-size: 12px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

export const SuccessMessage = styled.div`
  background: var(--success);
  color: var(--surface);
  padding: 15px 20px;
  border-radius: 25px;
  margin-bottom: 20px;
  display: ${(props) => (props.$show ? "flex" : "none")};
  align-items: center;
  gap: 10px;
  font-size: 14px;
  animation: ${(props) => (props.$show ? slideInRight : slideOutLeft)} 0.3s ease
    forwards;
`;

export const PaymentMethods = styled.div`
  margin-top: 30px;
  text-align: center;

  p {
    color: var(--text-muted);
    font-size: 12px;
    margin-bottom: 15px;
    text-transform: uppercase;
  }

  .icons {
    display: flex;
    justify-content: center;
    gap: 10px;
    opacity: 0.6;

    i {
      font-size: 24px;
      color: var(--text-muted);
    }
  }
`;
